import { useEffect, useState } from 'react';
import { Books } from '../../constants';
import fetchLocFromIP from '../../helpers/fetchLocFromIP';
import Error from '../../pages/book-link/error';
import { logFetchError, logRedirectStandalone, logUrlError } from '../../services/datadog';
import SwalService from '../../services/swal';
import { IBookRegionExtended, IUrlData } from './../../types';
import { getBetUrl } from './bet-place-page';
import ParlayErrorPage from './parlay-error-page';
import UrlFetchErrorPage from './url-fetch-error-page';

/*
|--------------------------------------------------------------------------
| React Page Component
|--------------------------------------------------------------------------
*/

export default function BetPlaceStandalonePage() {

    //set state variables
    const [region, setRegion] = useState('temp');
    const initialRegions: IBookRegionExtended[] = [];
    const initialUrls: IUrlData[] = [];
    const [bookRegions, setBookRegions] = useState(initialRegions);
    const [urls, setUrls] = useState(initialUrls);
    const [loading, setLoading] = useState(true);
    const regionBooks = ['BOOK_IPBQaQQTCRxplZx7SYOA','BOOK_pPg9ABaPSj2mL6qoMTKR1A','BOOK_88064cc6787c47ccbd4bbb036c7f55c5'];
    const UNSUPPORTED_REGION_BOOKS = [
        'BOOK_9e939c6df6e54dde8b7d01abdbf65e0a', // Kalshi
        'BOOK_ae54ed82802c445aa8dac91da2782010', // Polymarket
        'BOOK_e3910194923b4ad0bb50594a51358c47', // Novig
    ];

    const singleRgx = window.location.pathname.match(
        /^\/place\/(MRKT_[0-9a-zA-Z]+)\/(BOOK_[0-9a-zA-Z]+|[a-z]{2})$/
    );

    const parlayRgx = window.location.pathname.match(
        /^\/place\/parlay\/(BOOK_[0-9a-zA-Z]+|[a-z]{2})$/
    );

    const urlParams = new URLSearchParams(window.location.search);
    const marketSelection = urlParams.get('marketSelection');
    const line = urlParams.get('line'); //null or string

    var lines: (string | null)[];

    if (singleRgx) {
        // for single url structure, casts line / url as list to simplify later logic
        if (window.location.href.includes('BOOK_')){
            var [marketSelections, bookId] = [[singleRgx![1]], singleRgx![2]];
        } else {
            var [marketSelections, bookId] = [[singleRgx![1]], Books.find(x => x.abbr == singleRgx![2])?.id || ''];
        }
        var lines = [line];
    } else {
        //for parlay url structure, builds list of urls / lines from query parameters
        if (window.location.href.includes('BOOK_')){
            var bookId = parlayRgx![1];
        } else {
            var bookId = Books.find(x => x.abbr == parlayRgx![1])?.id || ''
        }
        var marketSelections = marketSelection!.split(',');
        if (line) lines = line.replace('None', '').replace('null', '').split(',');
        else lines = new Array(marketSelections.length).fill(null);
    }

    if (lines.length !== marketSelections.length) return <ParlayErrorPage />;

    //fetch loc from current IP
    const fetchLocation = async () => {
        var bookRegion: string | null = null;

        try {
            bookRegion = await fetchLocFromIP();
            let isValid = bookRegions?.some((r: any) => r.abbr === bookRegion);
            if (bookRegion && isValid) return bookRegion;
        } catch (err) {
            logFetchError('unhandled', null, err);
        }

        if (bookRegions.length === 0) {
            logUrlError(region, window.location.pathname, null);
            throw `No active regions for book: ${bookId}`;
        }
        //gets ordered list of valid regions, prompts with a swal
        let validRegions: { [key: string]: string } = {};
        bookRegions.sort((a, b) =>
            a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        );

        for (let br of bookRegions) validRegions[br.abbr] = br.name;
        await SwalService.selectRegionSwal(validRegions).then((result: any) => {
            bookRegion = result.value;
        });

        return bookRegion!;
    };

    const fetchBookRegions = async () => {
        let data: IBookRegionExtended[] = [];
        let r = await fetch(
            `${process.env.REACT_APP_API_BASE || ''}/v1/bookRegions?book=${bookId}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        data = await r.json();
        //filter out unsupported unless it's an odds-feed-only prediction market (Kalshi, Polymarket,
        //Novig), whose regions are all unsupported because they have no bet sync
        if (UNSUPPORTED_REGION_BOOKS.includes(bookId)) {
            return data;
        }
        return data.filter((x) => x.status !== 'unsupported');
    };

    const fetchUrls = async () => {
        //returns an empty list if either the market DNE or there are no urls for that book
        let urlList: IUrlData[] = [];
        for (let i = 0; i < marketSelections.length; i++) {
            const marketSelectionId = marketSelections[i];
            const line = lines[i];

            let data: IUrlData[] = [];
            let r = await fetch(
                `${
                    process.env.REACT_APP_API_BASE || ''
                }/v1/marketSelections/${marketSelectionId}/urls?book=${bookId}&line=${line}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_SUPERUSER_API_KEY}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            if (r.status === 200) data = await r.json();
            urlList = urlList.concat(data);
        }
        return urlList;
    };

    useEffect(() => {
        async function getBookRegions() {
            let callBookRegions = fetchBookRegions();
            let callUrls = fetchUrls();
            Promise.all([callBookRegions, callUrls])
                .then((data) => {
                    setBookRegions(data[0]);
                    setUrls(data[1]);
                })
                .catch((err) => {
                    //if this block is hit, neither state variables are set, and the splash page
                    //routes to whichever is first in the if/else block
                    console.log(`Error fetching data - ${err}`);
                })
                .finally(() => setLoading(false));
        }
        getBookRegions();
    }, []);

    useEffect(() => {
        async function getRegion() {
            await fetchLocation()
                .then((region) => {
                    setRegion(region);
                })
                .catch((err) => {
                    console.log(`Unable to fetch location - ${err}`);
                });
        }
        if (!loading && regionBooks.includes(bookId)) getRegion();
    }, [loading]);


    if (!loading && bookRegions.length == 0) {
        //TODO- change splash page? log?
        //CASE: no bookRegions for book
        console.log("ERROR: No bookRegions for book");
        return <Error />;
    } else if (!loading && urls.length == 0) {
        //TODO- change splash page? log?
        //CASE: no urls for market
        console.log("ERROR: No urls for market");
        return <UrlFetchErrorPage />;
    } else if (urls.length !== marketSelections.length && !loading) {
        //TODO- change splash page? log?
        //CASE: One of the markets has no urls, but others do
        console.log("ERROR: urls.length !== marketSelections.length", urls.length, "!==", marketSelections.length);
        return <UrlFetchErrorPage />;
    } else if (loading || (regionBooks.includes(bookId) && (!region || region === 'temp'))) {
        console.log("DEBUG: Still loading or waiting for region");
        return <div></div>;
    } else {
        //build url route block
        const bookRegion = bookRegions.find((br) => br.abbr === region);
        const regionAbbr = bookRegion ? bookRegion.abbr : '';
        const regionId = bookRegion ? bookRegion.id : 'No Region';

        logRedirectStandalone(marketSelections, bookId, lines, regionId, document.referrer);
        let betUrl = getBetUrl(regionAbbr, urls, bookRegions[0].book.abbr);
        window.location.href = betUrl;
    }

    //this should basically just be an error page IG? since it should just route?
    return <div></div>;
}
